import React, { FC } from 'react'
import { withRouter } from 'react-router-dom'
import { Segment, Grid } from 'semantic-ui-react'
import isEqual from 'lodash/isEqual'
import {
  dateTime as dateTimeFormatter,
  number as numberFormatter,
  date as dateFormatter,
} from 'loankeeper-common/helpers/formatters'
import dateComparer from 'loankeeper-common/helpers/dateComparer'

import { colors } from 'shared'
//@ts-ignore
import LKButton from 'shared/LKButton'

//@ts-ignore
import LKGrid from 'shared/LKGrid'
//@ts-ignore
import withConstantPermissions from 'shared/containers/withConstantPermissions'
//@ts-ignore
import { GridContainer, FakeInput } from 'shared/StyledComponents'
import { LKHeader } from 'loankeeper-common/components'
import { translateCreditInfo, translateBase, translateCmd, icon } from '../helpers'

import { constants as DCConstants } from '../../DetailsContainer'
import { CreditInfoProps } from '../types'

/* eslint-disable no-param-reassign */

const CreditInfoConfig = (pushDetailsRouteMiddleware) => ({
  instanceName: 'dbgrd_creditinfo',
  gridIdentity: {},

  columnConfigs: {
    FinishTime: { valueFormatter: ({ value }) => dateTimeFormatter(value) },
    StartTime: { valueFormatter: ({ value }) => dateTimeFormatter(value) },
  },

  enableColumnsResize: true,
  enableColumnsOrder: true,
  enableColumnsHide: false,
  enableRowSelection: true,
  syncGridState: false,

  getContextMenuItems: ({ node: { data } }) => [
    {
      name: translateCmd('view_loan'),
      action: () => pushDetailsRouteMiddleware({ ID: data.LoanID, ...data }),
      icon: 'info',
      disabled: !data.LoanID,
      permissionKey: 'LOAN_VIEW',
    },

    'excelExport',
  ],
})

const ActiveLoansGridConfig = (pushDetailsRoute) => ({
  gridIdentity: { formName: 'frm_client_briefing', objectName: 'dbgrd_activeloans' },

  columnConfigs: {
    OverdueAmount: { valueFormatter: ({ value }) => numberFormatter(value), cellStyle: { textAlign: 'right' } },
    OutstandingAmount: { valueFormatter: ({ value }) => numberFormatter(value), cellStyle: { textAlign: 'right' } },
    TotalCreditAmount: { valueFormatter: ({ value }) => numberFormatter(value), cellStyle: { textAlign: 'right' } },
    StandardInstalmentAmount: {
      valueFormatter: ({ value }) => numberFormatter(value),
      cellStyle: { textAlign: 'right' },
    },
    DisburseDate: { comparator: dateComparer, valueFormatter: ({ value }) => dateFormatter(value) },
    CurrentRegistrationDate: { valueFormatter: ({ value }) => dateFormatter(value) },
    CurrentOverdueDate: { valueFormatter: ({ value }) => dateFormatter(value) },
    OverdueDate: { valueFormatter: ({ value }) => dateFormatter(value) },
  },
  enableColumnsResize: true,
  enableColumnsOrder: true,
  enableColumnsHide: false,
  enableRowSelection: true,
  getRowStyle: ({
    node: {
      data: { StateID },
    },
  }) => {
    switch (StateID) {
      case 2:
        return { backgroundColor: colors.grey }
      case 4:
        return { backgroundColor: colors.black, color: '#fff' }
      case 6:
        return { backgroundColor: colors.yellow }
      default:
        return 'white'
    }
  },
  getContextMenuItems: ({ node: { data } }) => [
    { name: translateCmd('view_loan'), action: () => pushDetailsRoute(data), icon: 'info', permissionKey: 'LOAN_VIEW' },
    'excelExport',
  ],
})

const MemoizedCirequestsLKGrid: FC<{
  ID: string
  rowData: any[]
  pushDetailsRoute: (data: any) => void
}> = React.memo(({ ID, rowData, pushDetailsRoute }) => (
  <LKGrid
    rowData={rowData}
    {...CreditInfoConfig(pushDetailsRoute)}
    instanceName={`${DCConstants.DetailsGridPrefixes.client}-${ID}-creditinfo`}
  />
))

const MemoizedLoansLKGrid: FC<{
  name: string
  pushDetailsRoute: (data: { ID: string; HashSum_LoanID_: string; [key: string]: any }) => void
}> = React.memo(({ name, pushDetailsRoute }) => (
  <LKGrid
    {...ActiveLoansGridConfig(pushDetailsRoute)}
    instanceName={`${DCConstants.DetailsGridPrefixes.client}-${name}`}
  />
))

@withRouter
@withConstantPermissions('LOAN_VIEW as CanView')
class CreditInfo extends React.Component<CreditInfoProps> {
  shouldComponentUpdate(prevProps: CreditInfoProps) {
    return !isEqual(this.props, prevProps)
  }

  onRowDoubleClickedCurrentLoans = ({ node: { data } }) => {
    if (this.props.CanView) {
      this.pushDetailsRouteMiddleware({ ID: data.LoanID, ...data })
    }
  }

  onRowDoubleClickedLoans = ({ node: { data } }) => {
    if (this.props.CanView) {
      this.pushDetailsRoute(data)
    }
  }

  pushDetailsRoute = ({ ID, HashSum_LoanID_ }: { ID: string; HashSum_LoanID_: string }) => {
    this.props.history.push(`/secure/loan/${ID}/details/${HashSum_LoanID_}`)
  }

  pushDetailsRouteMiddleware(data: { ID: string; HashSum_LoanID_: string; [key: string]: any }) {
    if (data.ID) {
      this.pushDetailsRoute(data)
    }
  }

  render() {
    const {
      ID,
      data: { cirequests }, // loans,
      isModal,
    } = this.props

    return (
      <div className="u-flex u-directionColumn u-fluid" style={{ padding: 5, overflowX: 'hidden' }}>
        <div style={{ height: 'fit-content', margin: 5 }}>
          <GridContainer style={{ margin: '-5px' }}>
            <Grid style={{ flexDirection: 'column' }}>
              <Grid.Row>
                <Grid.Column textAlign="right" width={3}>
                  {translateCreditInfo('lbl_scoring')}
                </Grid.Column>
                <Grid.Column textAlign="left" width={2}>
                  <FakeInput>
                    <Segment>
                      <LKHeader size="small">&nbsp;</LKHeader>
                    </Segment>
                  </FakeInput>
                </Grid.Column>
                <Grid.Column textAlign="right" width={3}>
                  {translateCreditInfo('lbl_active_loans')}
                </Grid.Column>
                <Grid.Column textAlign="left" width={2}>
                  <FakeInput>
                    <Segment>
                      <LKHeader size="small">&nbsp;</LKHeader>
                    </Segment>
                  </FakeInput>
                </Grid.Column>
                <Grid.Column textAlign="right" width={4}>
                  {translateCreditInfo('lbl_active_loans_as_guarantor')}
                </Grid.Column>
                <Grid.Column textAlign="left" width={2}>
                  <FakeInput>
                    <Segment>
                      <LKHeader size="small">&nbsp;</LKHeader>
                    </Segment>
                  </FakeInput>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column textAlign="right" width={3}>
                  {translateCreditInfo('lbl_last_update')}
                </Grid.Column>
                <Grid.Column textAlign="left" width={2}>
                  <FakeInput>
                    <Segment>
                      <LKHeader size="small">&nbsp;</LKHeader>
                    </Segment>
                  </FakeInput>
                </Grid.Column>
                <Grid.Column textAlign="right" width={3}>
                  {translateCreditInfo('lbl_closed_loans')}
                </Grid.Column>
                <Grid.Column textAlign="left" width={2}>
                  <FakeInput>
                    <Segment>
                      <LKHeader size="small">&nbsp;</LKHeader>
                    </Segment>
                  </FakeInput>
                </Grid.Column>
                <Grid.Column textAlign="right" width={4}>
                  {translateCreditInfo('lbl_closed_loans_as_guarantor')}
                </Grid.Column>
                <Grid.Column textAlign="left" width={2}>
                  <FakeInput>
                    <Segment>
                      <LKHeader size="small">&nbsp;</LKHeader>
                    </Segment>
                  </FakeInput>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </GridContainer>
        </div>
        <div className="u-flex u-directionColumn u-fluid" style={{ maxHeight: '84%' }}>
          <div className="u-fluid u-flex">
            <div className="u-fluid u-relative" style={{ maxWidth: '30%', maxHeight: '95%', textAlign: 'center' }}>
              <div className="u-fluid u-directionColumn u-flex">
                <LKButton alias="refresh" style={{ marginRight: 0 }}>
                  {translateCreditInfo('cmd_req_data_creditinfo')}
                </LKButton>
              </div>
              <Segment
                className="u-fluid u-directionColumn u-flex"
                style={{ height: `${isModal ? 97 : 100}%`, margin: 0, padding: 0 }}
              >
                <MemoizedCirequestsLKGrid
                  ID={ID}
                  rowData={cirequests}
                  pushDetailsRoute={this.pushDetailsRouteMiddleware}
                />
              </Segment>
            </div>

            <div className="u-flex u-directionColumn u-fluid">
              <Segment style={{ height: '50%', margin: 0, padding: 0 }}>
                <MemoizedLoansLKGrid pushDetailsRoute={this.pushDetailsRouteMiddleware} name={`${ID}-ActiveLoans`} />
              </Segment>

              <Segment style={{ height: '50%', margin: 0, padding: 0 }}>
                <MemoizedLoansLKGrid pushDetailsRoute={this.pushDetailsRouteMiddleware} name={`${ID}-ClosedLoans`} />
              </Segment>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

//@ts-ignore
CreditInfo.config = {
  title: () => translateBase('tb_CreditInfo'),
  name: 'creditinfo',
  component: CreditInfo,
  api: ['cirequests'], // 'loans'
  icon: icon('tb_creditinfo'),
}

//@ts-ignore
CreditInfo.defaultProps = {
  data: [],
}

export default CreditInfo
